import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { planLabel } from "../../utils/plans";
import { Dialog, DialogContent, IconButton, Tooltip } from "@mui/material";
import { FileDownloadOutlined, VisibilityOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import axiosInstant from "../../api";
import {
    salesLeadDeleteEndpoint,
    salesLeadsEndpoint,
    salesPdpAuditReportsEndpoint,
    salesPdpAuditReportsJsonEndpoint,
    salesPrepPackEndpoint,
    salesPrepPackRegenerateEndpoint,
    salesPrepPacksEndpoint,
} from "../../api/endpoints";
import { MainContainer } from "../../components/ContentWrapper";
import { ListPageHeader } from "../../components/ListPageHeader/ListPageHeader";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { TabHeader } from "../../components/Tabs/TabHeader";
import { DataTable, DataTableColumn } from "../../components/DataTable/DataTable";
import {
    IconDownload,
    IconMail,
    IconPencil,
    IconPlus,
    IconReport,
    IconTrash,
} from "../../utils/icons";
import { useCommonParams } from "../../hooks/useCommonParams";
import { useResourceList } from "../../hooks/useResourceList";
import { PDP_AUDIT_URL } from "../../components/Header/configuration/navs";
import { Lead, PdpAuditReport } from "./useSalesData";
import { PdpAuditReport as PdpAuditReportView } from "../../components/PdpAudit/PdpAuditReport";
import { MultiAuditSynthesisReport } from "../../components/PdpAudit/MultiAuditSynthesisReport";
import { PdpAudit, PdpAuditResponse } from "../../components/PdpAudit/types";
import {
    RmnCategoryReportParams,
    RmnComparisonReportParams,
    RmnMultiAuditReport,
} from "../../types/rmn";
import { ProposalFlowModal } from "../../components/ProposalFlow/ProposalFlowModal";
import { ProposalsTab } from "./proposals/ProposalsTab";
import DownloadLeadsCsv from "./DownloadLeadsCsv";
import { downloadAuditReportPdf } from "./reportPdf";
import { ConsumerVoiceReportsTab } from "./ConsumerVoiceReportsTab";
import { AiPresenceReportsTab } from "./AiPresenceReportsTab";
import PrepPackList from "../../components/PrepPack/PrepPackList";
import { RequestedByCell } from "../../components/RequestedByCell";

// Consumer Voice ad-hoc runs use the portal's own public tool (no captcha or
// email for a signed-in session), where the PDP audit opens the marketing
// site's embed.
const CONSUMER_VOICE_AUDIT_PATH = "/consumer-voice-audit";
const AI_PRESENCE_AUDIT_PATH = "/ai-presence-audit";

type Tab = "leads" | "reports" | "cv" | "aip" | "bi" | "proposals";
const PAGE_SIZE = 25;

const PERIOD_LABELS: Record<string, string> = {
    today: "Today",
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    quarter: "Last quarter",
    year: "Last year",
    "5y": "Last 5 years",
    "10y": "Last 10 years",
    custom: "Custom range",
};

const TAB_FROM_PATH: Record<string, Tab> = {
    "/sales/leads": "leads",
    "/sales/pdp-audit-reports": "reports",
    "/sales/consumer-voice-reports": "cv",
    "/sales/ai-presence-reports": "aip",
    "/sales/prep-packs": "bi",
    "/sales/proposals": "proposals",
};
const PATH_FROM_TAB: Record<Tab, string> = {
    leads: "/sales/leads",
    reports: "/sales/pdp-audit-reports",
    cv: "/sales/consumer-voice-reports",
    aip: "/sales/ai-presence-reports",
    bi: "/sales/prep-packs",
    proposals: "/sales/proposals",
};
const TAB_ORDER: Tab[] = ["leads", "reports", "cv", "aip", "bi", "proposals"];

const formatDate = (iso: string) => (iso ? dayjs(iso).format("MMM DD, YYYY h:mm A") : "—");
// Row icon buttons, as on the Consumer Voice / AI Presence tabs.
const icon = "w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed";

interface TabProps {
    searchText: string;
    searchParams: URLSearchParams;
    setSearchParams: ReturnType<typeof useCommonParams>["setSearchParams"];
    page: number;
    setPage: (n: number) => void;
    onCountChange: (n: number) => void;
    extraParams?: Record<string, string | number | undefined>;
}

function LeadsTab(props: TabProps) {
    const navigate = useNavigate();
    const [pendingDelete, setPendingDelete] = useState<Lead | null>(null);
    const [deleting, setDeleting] = useState(false);

    const list = useResourceList<Lead>({
        endpoint: salesLeadsEndpoint,
        pageSize: PAGE_SIZE,
        errorLabel: "Failed to load leads",
        ...props,
    });

    const handleDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await axiosInstant.delete(salesLeadDeleteEndpoint(pendingDelete.id));
            toast.success("Lead deleted successfully!");
            setPendingDelete(null);
            await list.initLoad(1);
        } catch {
            toast.error("Failed to delete lead");
        } finally {
            setDeleting(false);
        }
    };

    const columns: DataTableColumn<Lead>[] = [
        { key: "email", header: "Email", render: (l) => <span className="text-accent-text font-medium">{l.email}</span> },
        {
            key: "subscribed",
            header: "Subscribed",
            render: (l) => (
                <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold ${
                        l.subscribed ? "bg-up-bg text-up" : "bg-surface-2 text-text-2"
                    }`}
                >
                    {l.subscribed ? "Yes" : "No"}
                </span>
            ),
        },
        {
            key: "source",
            header: "Source",
            render: (l) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold bg-[#EEF1F5] text-[#3B5266] dark:bg-surface-2 dark:text-text-2">
                    {l.source}
                </span>
            ),
        },
        {
            key: "account",
            header: "Account",
            render: (l) =>
                l.account ? (
                    <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold bg-up-bg text-up">
                            Yes
                        </span>
                        {l.account.brand && (
                            <Link
                                to={`/brands/${l.account.brand.id}`}
                                title={`Open brand ${l.account.brand.name}`}
                                className="text-accent-text text-[12px] font-medium hover:underline"
                            >
                                {l.account.brand.name}
                            </Link>
                        )}
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold bg-surface-2 text-text-2">
                        No
                    </span>
                ),
        },
        {
            key: "plan",
            header: "Plan",
            render: (l) =>
                l.account && l.account.brand && l.account.brand.plan ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold bg-tint text-accent-text">
                        {planLabel(l.account.brand.plan)}
                    </span>
                ) : (
                    <span className="text-text-2 text-[12px]">None</span>
                ),
        },
        {
            key: "created",
            header: "Created",
            render: (l) => formatDate(l.created_at),
            className: "text-text-2 text-[12px] font-mono",
        },
        {
            key: "related",
            header: "Related",
            render: (l) => (
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        title={`View PDP audits for ${l.email}`}
                        onClick={() =>
                            navigate(`/sales/pdp-audit-reports?search=${encodeURIComponent(l.email)}`)
                        }
                        className="px-2.5 py-1 rounded-full bg-[#EEF3FA] text-[11px] font-semibold text-[#3B6EA0] dark:bg-[#3B6EA0]/30 dark:text-[#A8CBEC] cursor-pointer hover:bg-[#DFE9F5] dark:hover:bg-[#3B6EA0]/45 border-0"
                    >
                        🔍 Audits
                    </button>
                    <button
                        type="button"
                        title={`View proposals for ${l.email}`}
                        onClick={() =>
                            navigate(`/sales/proposals?search=${encodeURIComponent(l.email)}`)
                        }
                        className="px-2.5 py-1 rounded-full bg-tint text-[11px] font-semibold text-accent-text cursor-pointer hover:bg-[#F3E9DA] border-0"
                    >
                        📋 Strategies
                    </button>
                </div>
            ),
        },
        {
            key: "actions",
            header: "",
            width: 40,
            render: (l) => (
                <button
                    type="button"
                    title="Delete lead"
                    onClick={() => setPendingDelete(l)}
                    className="w-8 h-8 rounded-md text-muted flex items-center justify-center cursor-pointer hover:text-down hover:bg-down-bg transition-colors"
                >
                    <IconTrash width={16} height={16} stroke="currentColor" />
                </button>
            ),
        },
    ];

    return (
        <>
            <DataTable<Lead>
                columns={columns}
                rows={list.items}
                loading={list.loading}
                totalCount={list.totalCount}
                hasMore={list.hasMore}
                error={list.error}
                onLoadMore={list.loadMore}
                emptyMessage="No leads found"
            />
            {pendingDelete && (
                <ConfirmationDialog
                    open
                    title="Delete Lead"
                    description={`Are you sure you want to delete the lead "${pendingDelete.email}"? This will also remove any PDP audit reports linked to it.`}
                    confirmLabel="Delete"
                    onClose={() => setPendingDelete(null)}
                    onConfirm={handleDelete}
                    disabled={deleting}
                />
            )}
        </>
    );
}

// Report-type filter chips (RMN async category/comparison reports surface in
// this list alongside the classic single-PDP audits).
const REPORT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: "All" },
    { value: "pdp", label: "PDP" },
    { value: "category", label: "Category" },
    { value: "comparison", label: "Comparison" },
    { value: "brand", label: "Brand" },
];

const REPORT_TYPE_LABELS: Record<string, string> = {
    pdp: "PDP",
    category: "Category",
    comparison: "Comparison",
    brand: "Brand",
};

// Rebuild the response shape the single-audit renderer expects from a stored
// pdp_audit_reports row (same as the RMN portal's PastAudits list).
const reportRowToResponse = (r: PdpAuditReport): PdpAuditResponse => ({
    audit: r.report as PdpAudit,
    meta: {
        url: r.url,
        finalUrl: r.url,
        title: "",
        metaDescription: "",
        jsonLdBlockCount: 0,
        geminiStatus: "success",
        geminiStatusCode: null,
        geminiError: null,
        reportId: r.id,
    },
});

const reportRowLabel = (r: PdpAuditReport): string => {
    if (r.report_type === "category") {
        return (r.params as RmnCategoryReportParams | null | undefined)?.category_name || r.url;
    }
    if (r.report_type === "comparison") {
        const urls = (r.params as RmnComparisonReportParams | null | undefined)?.urls || [];
        return urls.length ? `${urls.length} pages · ${urls[0]}` : r.url;
    }
    return r.url;
};

function ReportsTab(props: TabProps) {
    const { searchParams, setSearchParams, extraParams } = props;
    const navigate = useNavigate();
    const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);
    const [proposalFor, setProposalFor] = useState<PdpAuditReport | null>(null);
    const [viewing, setViewing] = useState<PdpAuditReport | null>(null);
    const [exporting, setExporting] = useState(false);

    // Deep-linked type filter; changing a non-ignored search param makes
    // usePagination reset to page 1 and refetch.
    const typeFilter = searchParams.get("report_type") || "";
    const setTypeFilter = (value: string) => {
        setSearchParams((prev) => {
            if (value) prev.set("report_type", value);
            else prev.delete("report_type");
            return prev;
        });
    };

    const list = useResourceList<PdpAuditReport>({
        endpoint: salesPdpAuditReportsEndpoint,
        pageSize: PAGE_SIZE,
        errorLabel: "Failed to load reports",
        ...props,
        extraParams: { ...extraParams, report_type: typeFilter || undefined },
    });

    // One JSON file with every report matching the active filters (search,
    // type chip, period) — the server applies the same criteria as the list.
    const handleExportJson = async () => {
        setExporting(true);
        try {
            const params: Record<string, string> = {};
            if (props.searchText) params.search = props.searchText;
            if (typeFilter) params.report_type = typeFilter;
            if (extraParams?.period) params.period = String(extraParams.period);
            if (extraParams?.from) params.from = String(extraParams.from);
            if (extraParams?.to) params.to = String(extraParams.to);
            const res = await axiosInstant.get(salesPdpAuditReportsJsonEndpoint, {
                params,
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data as Blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `pdp-audit-reports-${dayjs().format("YYYY-MM-DD")}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("JSON exported");
        } catch {
            toast.error("Failed to export JSON");
        } finally {
            setExporting(false);
        }
    };

    const handleDownloadPdf = async (r: PdpAuditReport) => {
        setPdfLoadingId(r.id);
        try {
            await downloadAuditReportPdf(r);
            toast.success("PDF downloaded");
        } catch {
            toast.error("Failed to generate PDF");
        } finally {
            setPdfLoadingId(null);
        }
    };

    // Legacy rows predate report_type/status columns — treat them as ready
    // single-PDP audits.
    const isPdp = (r: PdpAuditReport) => !r.report_type || r.report_type === "pdp";

    const columns: DataTableColumn<PdpAuditReport>[] = [
        // Who ran it: the signed-in user, or the lead for public-tool runs.
        { key: "email", header: "Email", render: (r) => <RequestedByCell user={r.user} lead={r.lead} /> },
        {
            key: "type",
            header: "Type",
            render: (r) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-[10px] text-[11px] font-semibold bg-surface-2 text-text-2">
                    {REPORT_TYPE_LABELS[r.report_type || "pdp"] || r.report_type}
                </span>
            ),
        },
        {
            key: "url",
            header: "URL audited",
            className: "text-[12px] text-text-2 max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap",
            render: (r) => {
                const compareUrls =
                    r.report_type === "comparison"
                        ? (r.params as RmnComparisonReportParams | null | undefined)?.urls || []
                        : [];
                return (
                    <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={compareUrls.length ? compareUrls.join("\n") : r.url}
                    >
                        {reportRowLabel(r)}
                    </a>
                );
            },
        },
        {
            key: "status",
            header: "Status",
            render: (r) => {
                if (r.status === "generating") {
                    return (
                        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1">
                            Running…
                        </span>
                    );
                }
                if (r.status === "error") {
                    return (
                        <Tooltip title={r.generation_error || "Report generation failed"}>
                            <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#c62828] bg-[#FBEFEF] rounded-full px-2.5 py-1">
                                Failed
                            </span>
                        </Tooltip>
                    );
                }
                if (!r.report) return <span className="text-muted text-[12px]">—</span>;
                return (
                    <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#2E7D32] bg-[#E8F5E9] rounded-full px-2.5 py-1">
                        Ready
                    </span>
                );
            },
        },
        {
            key: "proposal",
            header: "Strategy",
            render: (r) =>
                r.proposals && r.proposals.length > 0 ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {r.proposals.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                title={p.name}
                                onClick={() => navigate(`/sales/proposals/${p.id}`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tint text-[11px] font-semibold text-accent-text cursor-pointer hover:bg-[#F3E9DA] border-0"
                            >
                                📋 #{p.id}
                                {p.status !== "proposal" ? " · converted" : ""}
                            </button>
                        ))}
                    </div>
                ) : (
                    <span className="text-muted text-[12px]">—</span>
                ),
        },
        {
            key: "created",
            header: "Created",
            render: (r) => <span className="text-[12px] text-text-2 whitespace-nowrap">{formatDate(r.created_at)}</span>,
        },
        {
            // Same row actions as the Consumer Voice / AI Presence tabs.
            key: "actions",
            header: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1">
                    <Tooltip title="View report">
                        <span>
                            <button
                                type="button"
                                aria-label="View report"
                                disabled={!r.report}
                                onClick={() => setViewing(r)}
                                className={icon}
                            >
                                <VisibilityOutlined sx={{ fontSize: 15 }} />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title={isPdp(r) ? "Download PDF" : "PDF download is for single-page audits"}>
                        <span>
                            <button
                                type="button"
                                aria-label="Download PDF"
                                disabled={!r.report || !isPdp(r) || pdfLoadingId === r.id}
                                onClick={() => handleDownloadPdf(r)}
                                className={`${icon} ${pdfLoadingId === r.id ? "animate-pulse" : ""}`}
                            >
                                <FileDownloadOutlined sx={{ fontSize: 15 }} />
                            </button>
                        </span>
                    </Tooltip>
                    <button
                        type="button"
                        title={isPdp(r) ? "Start a strategy from this audit" : "Strategies start from a single-page audit"}
                        disabled={!r.report || !isPdp(r)}
                        onClick={() => setProposalFor(r)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border-token rounded-md bg-surface text-[11.5px] font-medium text-accent-text cursor-pointer hover:border-accent hover:bg-tint disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Create strategy
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="flex items-center gap-1.5 mb-4">
                {REPORT_TYPE_OPTIONS.map((opt) => {
                    const active = typeFilter === opt.value;
                    return (
                        <button
                            key={opt.value || "all"}
                            type="button"
                            onClick={() => setTypeFilter(opt.value)}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold cursor-pointer border-0 transition-colors ${
                                active
                                    ? "bg-tint text-accent-text"
                                    : "bg-surface-2 text-text-2 hover:text-text-1"
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
                <button
                    type="button"
                    title="Download all reports matching the current filters as one JSON file"
                    disabled={exporting}
                    onClick={handleExportJson}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-border-token rounded-md bg-surface text-[12px] font-semibold text-text-2 cursor-pointer hover:border-accent hover:text-text-1 disabled:opacity-60 disabled:cursor-default"
                >
                    <IconDownload width={14} height={14} stroke="currentColor" />
                    {exporting ? "Exporting…" : "Export JSON"}
                </button>
            </div>
            <DataTable<PdpAuditReport>
                columns={columns}
                rows={list.items}
                loading={list.loading}
                totalCount={list.totalCount}
                hasMore={list.hasMore}
                error={list.error}
                onLoadMore={list.loadMore}
                emptyMessage="No reports found"
            />
            <Dialog open={!!viewing} onClose={() => setViewing(null)} fullWidth maxWidth="md">
                <IconButton
                    onClick={() => setViewing(null)}
                    sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                    aria-label="Close report"
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 0 }}>
                    {viewing && viewing.report && (
                        isPdp(viewing) ? (
                            <PdpAuditReportView data={reportRowToResponse(viewing)} isLoggedIn />
                        ) : (
                            <MultiAuditSynthesisReport
                                reportType={viewing.report_type || ""}
                                report={viewing.report as RmnMultiAuditReport<PdpAudit>}
                                params={
                                    viewing.params as
                                        | RmnCategoryReportParams
                                        | RmnComparisonReportParams
                                        | null
                                }
                            />
                        )
                    )}
                </DialogContent>
            </Dialog>
            {proposalFor && (
                <ProposalFlowModal
                    open
                    onClose={() => setProposalFor(null)}
                    product={{
                        title: proposalFor.report?.productName || undefined,
                        url: proposalFor.url,
                        brand_name: proposalFor.report?.brandName || undefined,
                    }}
                    pdpAuditReportId={proposalFor.id}
                    onCreated={() => {
                        toast.success("Strategy created — see Sales ▸ Strategies");
                    }}
                />
            )}
        </>
    );
}

export default function SalesAdmin() {
    const location = useLocation();
    const navigate = useNavigate();
    const tab: Tab = TAB_FROM_PATH[location.pathname] || "leads";

    const { searchParams, setSearchParams, searchText, setSearchText, page, setPage } = useCommonParams();
    const [searchInput, setSearchInput] = useState(searchText);
    const [leadsCount, setLeadsCount] = useState(0);
    const [reportsCount, setReportsCount] = useState(0);
    const [cvCount, setCvCount] = useState(0);
    const [aipCount, setAipCount] = useState(0);
    const [biCount, setBiCount] = useState(0);
    const [proposalsCount, setProposalsCount] = useState(0);
    const [exportOpen, setExportOpen] = useState(false);
    const [newProposalOpen, setNewProposalOpen] = useState(false);
    const [newBiOpen, setNewBiOpen] = useState(false);

    useEffect(() => {
        if (searchInput === searchText) return;
        const t = setTimeout(() => setSearchText(searchInput), 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (searchText !== searchInput) setSearchInput(searchText);
    }, [searchText]);

    const changeTab = (next: Tab) => {
        if (next === tab) return;
        setSearchInput("");
        setSearchParams(() => new URLSearchParams(), { replace: false });
        navigate(PATH_FROM_TAB[next]);
    };

    const extraParams = {
        period: searchParams.get("period") || undefined,
        from: searchParams.get("from") || undefined,
        to: searchParams.get("to") || undefined,
    };

    const sharedProps = { searchParams, setSearchParams, page, setPage, searchText, extraParams };

    const clearPeriodFilter = () => {
        setSearchParams((prev) => {
            prev.delete("period");
            prev.delete("from");
            prev.delete("to");
            return prev;
        });
    };

    const tabs = useMemo(
        () => [
            { value: 0, text: `Leads${leadsCount ? ` (${leadsCount})` : ""}`, Icon: IconMail },
            { value: 1, text: `PDP Audit Reports${reportsCount ? ` (${reportsCount})` : ""}`, Icon: IconReport },
            { value: 2, text: `Consumer Voice Reports${cvCount ? ` (${cvCount})` : ""}`, Icon: IconReport },
            { value: 3, text: `AI Presence Reports${aipCount ? ` (${aipCount})` : ""}`, Icon: IconReport },
            { value: 4, text: `Prep Packs${biCount ? ` (${biCount})` : ""}`, Icon: IconReport },
            { value: 5, text: `Strategies${proposalsCount ? ` (${proposalsCount})` : ""}`, Icon: IconPencil },
        ],
        [leadsCount, reportsCount, cvCount, aipCount, biCount, proposalsCount],
    );

    // One header action per tab.
    const HEADER_ACTIONS: Record<Tab, { label: string; onClick: () => void; icon: React.ReactNode }> = {
        leads: {
            label: "Export CSV",
            onClick: () => setExportOpen(true),
            icon: <IconDownload width={16} height={16} stroke="#FFF" />,
        },
        reports: {
            label: "New PDP Audit",
            onClick: () => window.open(PDP_AUDIT_URL, "_blank", "noopener,noreferrer"),
            icon: <IconReport width={16} height={16} stroke="#FFF" />,
        },
        cv: {
            label: "New CV Analysis",
            onClick: () => window.open(CONSUMER_VOICE_AUDIT_PATH, "_blank", "noopener,noreferrer"),
            icon: <IconReport width={16} height={16} stroke="#FFF" />,
        },
        aip: {
            label: "New AI Presence Analysis",
            onClick: () => window.open(AI_PRESENCE_AUDIT_PATH, "_blank", "noopener,noreferrer"),
            icon: <IconReport width={16} height={16} stroke="#FFF" />,
        },
        bi: {
            label: "New Sales Intel Prep Pack",
            onClick: () => setNewBiOpen(true),
            icon: <IconPlus width={16} height={16} stroke="#FFF" />,
        },
        proposals: {
            label: "New Strategy",
            onClick: () => setNewProposalOpen(true),
            icon: <IconPlus width={16} height={16} stroke="#FFF" />,
        },
    };
    const headerAction = HEADER_ACTIONS[tab];
    const SEARCH_PLACEHOLDERS: Partial<Record<Tab, string>> = {
        reports: "Search by email",
        cv: "Search by email",
        aip: "Search by email",
        proposals: "Search by name or email",
        bi: "Search by brand",
    };

    return (
        <MainContainer>
            <ListPageHeader
                title="Sales"
                subtitle="Leads, PDP audit reports, Consumer Voice and AI Presence reports, Sales Intel Prep Packs, and strategies"
                actionLabel={headerAction.label}
                onActionClick={headerAction.onClick}
                actionIcon={headerAction.icon}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                searchInputId="search-sales"
                searchPlaceholder={SEARCH_PLACEHOLDERS[tab] || "Search by email"}
            />

            <TabHeader
                tabs={tabs}
                selectedTab={TAB_ORDER.indexOf(tab)}
                setSelectedTab={(idx) => changeTab(TAB_ORDER[idx] || "leads")}
                variant="underline"
                containerClassName="border-b border-border-token mb-4"
            />

            {(extraParams.period || extraParams.from) && (
                <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-2 bg-tint text-accent-text text-[12.5px] font-semibold px-3 py-1.5 rounded-full">
                        {PERIOD_LABELS[extraParams.period ?? ""] ?? "Selected period"}
                        <button
                            type="button"
                            onClick={clearPeriodFilter}
                            className="text-accent-text hover:text-text-1 cursor-pointer leading-none text-[15px]"
                            title="Clear filter"
                        >
                            ×
                        </button>
                    </span>
                </div>
            )}

            {tab === "leads" && <LeadsTab {...sharedProps} onCountChange={setLeadsCount} />}
            {tab === "reports" && <ReportsTab {...sharedProps} onCountChange={setReportsCount} />}
            {tab === "cv" && <ConsumerVoiceReportsTab {...sharedProps} onCountChange={setCvCount} />}
            {tab === "aip" && <AiPresenceReportsTab {...sharedProps} onCountChange={setAipCount} />}
            {tab === "bi" && (
                <PrepPackList
                    endpoints={{
                        list: salesPrepPacksEndpoint,
                        item: salesPrepPackEndpoint,
                        regenerate: salesPrepPackRegenerateEndpoint,
                    }}
                    allowDelete
                    productsNavigable
                    internal
                    embedded={{ modalOpen: newBiOpen, onCloseModal: () => setNewBiOpen(false), onCountChange: setBiCount }}
                />
            )}
            {tab === "proposals" && (
                <ProposalsTab
                    {...sharedProps}
                    onCountChange={setProposalsCount}
                    modalOpen={newProposalOpen}
                    onCloseModal={() => setNewProposalOpen(false)}
                />
            )}

            <DownloadLeadsCsv open={exportOpen} onClose={() => setExportOpen(false)} search={searchText} />
        </MainContainer>
    );
}
